import * as React from 'react';
import styled from 'styled-components';
import { useMemo } from 'react';
import { conditional } from '@/utils/conditional';
import { breakpoints } from '@/components/responsive';

export interface StrapiTextualProps {
  text: string;
  mobileText?: string;
  style?: React.CSSProperties;
  weight?: 'light' | 'normal' | 'bold';
  lineHeight?: string;
  size?: string;
  href?: string;
  className?: string;
  onClick?: () => void;
  xlMultiplier?: number;
  mobileMultiplier?: number;
  smMobileMultiplier?: number;
  openInNewTab?: boolean;
  highlight?: string;
  highlightStyle?: React.CSSProperties;
}

export function StrapiTextual({
  text,
  mobileText,
  style,
  weight,
  size,
  href,
  lineHeight,
  className,
  onClick,
  xlMultiplier,
  mobileMultiplier,
  smMobileMultiplier,
  highlight,
  highlightStyle,
  openInNewTab,
}: StrapiTextualProps) {
  const applyHighlight = (inputText: string) => {
    if (!highlight) return inputText;
    const parts = inputText.split(new RegExp(`(${highlight})`, `gi`));
    return parts.map((part, index) =>
      part.toLowerCase() === highlight.toLowerCase() ? (
        <HighlightSpan key={index} style={highlightStyle}>
          {part}
        </HighlightSpan>
      ) : (
        part
      ),
    );
  };

  const formattedText = useMemo(
    () => text?.replace(/ /g, `\u00A0`).replace(/\t/g, `\u2B7E`),
    [text],
  );

  const splitText = useMemo(
    () => formattedText?.split(`\n`).map(applyHighlight),
    [formattedText, highlight],
  );

  const formattedMobileText = useMemo(
    () => mobileText?.replace(/ /g, `\u00A0`).replace(/\t/g, `\u2B7E`),
    [mobileText],
  );

  const splitMobileText = useMemo(
    () => formattedMobileText?.split(`\n`).map(applyHighlight),
    [formattedMobileText, highlight],
  );

  return (
    <>
      <Wrapper
        className={`${className} strapi-textual only-desktop`}
        target={openInNewTab ? `_blank` : undefined}
        href={href}
        style={style}
        weight={weight}
        size={size}
        lineHeight={lineHeight}
        onClick={onClick}
        xlMultiplier={xlMultiplier}
        mobileMultiplier={mobileMultiplier}
      >
        {splitText.map((line, index) => (
          <React.Fragment key={index}>
            {line}
            {splitText?.length > 1 && <br />}
          </React.Fragment>
        ))}
      </Wrapper>
      <Wrapper
        className={`${className} strapi-textual only-mobile`}
        href={href}
        target={openInNewTab ? `_blank` : undefined}
        style={style}
        weight={weight}
        size={size}
        lineHeight={lineHeight}
        onClick={onClick}
        xlMultiplier={xlMultiplier}
        mobileMultiplier={mobileMultiplier}
        smMobileMultiplier={smMobileMultiplier}
      >
        {mobileText &&
          splitMobileText.map((line, index) => (
            <React.Fragment key={index}>
              {line}
              {splitMobileText?.length > 1 && <br />}
            </React.Fragment>
          ))}
        {!mobileText && text}
      </Wrapper>
    </>
  );
}

const Wrapper = styled.a`
  font-family: Eesti, sans-serif;
  font-weight: ${(props: StrapiTextualProps) =>
    conditional(props.weight, props.weight === `light`, 300) || `normal`};
  font-size: ${(props: StrapiTextualProps) => props.size || `1.2rem`};
  text-align: center;
  text-decoration: unset;
  appearance: none;
  color: inherit;
  user-select: ${(props: StrapiTextualProps) => (props.href ? `none` : `auto`)};
  line-height: ${(props: StrapiTextualProps) => props.lineHeight || `inherit`};

  cursor: ${(props: StrapiTextualProps) =>
    props.href || props.onClick ? `pointer` : `auto`};

  :hover {
    opacity: ${(props: StrapiTextualProps) => (props.href ? 0.8 : 1)};
  }

  :active {
    opacity: ${(props: StrapiTextualProps) => (props.href ? 0.6 : 1)};
  }

  ${breakpoints.up(`xlrm`)} {
    font-size: calc(
      ${(props: StrapiTextualProps) => props.size || `1.2rem`} *
        ${(props: StrapiTextualProps) => props.xlMultiplier || 1}
    );
  }

  ${breakpoints.down(`md`)} {
    font-size: calc(
      ${(props: StrapiTextualProps) => props.size || `1.2rem`} *
        ${(props: StrapiTextualProps) => props.mobileMultiplier || 0.7}
    );
  }

  ${breakpoints.down(365)} {
    font-size: calc(
      ${(props: StrapiTextualProps) => props.size || `1.2rem`} *
        ${(props: StrapiTextualProps) => props.smMobileMultiplier || 0.7}
    );
  }
`;

const HighlightSpan = styled.span`
  ${(props) => props.style && { ...props.style }};
`;
